import React, { useEffect } from 'react';
import { Save, Trash2, CirclePlus } from 'lucide-react';
import {
  useForm,
  useFieldArray,
  Controller,
  FormProvider,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Select from 'react-select';

import {
  currencyOptions,
  CURRENCY_SYMBOLS,
  invoiceStatusOptions,
  lineItemTypeOptions,
} from '../../constants';
import { IAgent } from '../../types/agent';
import { Invoice } from '../../types/invoice';
import {
  InvoiceFormInputs,
  invoiceFormSchema,
  CreateInvoicePayload,
} from '../../validations/invoice';
import {
  parseDate,
  getTodayDate,
  getCurrentDateTime,
  getBillDate,
  getCurrentMonthFirstDate,
  getCurrentMonthLastDate,
  getAgentInvoiceBillDate,
  getAgentInvoiceDueDate,
  getClientInvoiceBillDate,
  getClientInvoiceDueDate,
  getBrandsForClient,
  filterAgentsForBrands,
  getAgentPay,
} from '../../utils';

import { BtnPrimary } from '../common/Buttons';
import InputComponent from '../common/Input';
import {
  fetchAgentList,
  fetchBrandList,
  fetchClientList,
  fetchAgentBrandList,
} from '../../hooks/queries';
import { createInvoice, updateInvoice } from '../../hooks/mutations';

const backendUrl = import.meta.env.VITE_CHOCO_URL;
interface InvoiceFormProps {
  invoice?: Invoice;
  title?: string;
  isEditing?: boolean;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  isAgent?: boolean;
}

const InvoiceForm: React.FC<InvoiceFormProps> = ({
  invoice,
  isEditing = false,
  onSuccess,
  onError,
  isAgent = false,
}) => {
  const { data: clients } = fetchClientList();
  const { data: agents } = fetchAgentList();
  const { data: brands } = fetchBrandList();
  const { data: agentBrands } = fetchAgentBrandList();

  const { mutate: create, isPending: isCreating } = createInvoice({
    onSuccess: () => onSuccess?.(),
    onError: (error) => onError?.(error),
  });

  const { mutate: update, isPending: isUpdating } = updateInvoice(
    invoice?.id || '',
    {
      onSuccess: () => onSuccess?.(),
      onError: (error) => onError?.(error),
    }
  );

  // Parse and format date helper
  const parseAndFormatDate = (dateString: string | undefined): string => {
    if (!dateString) return '';

    // Handle 'DD/MM/YYYY' format specifically
    const parts = dateString.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (parts) {
      return `${parts[3]}-${parts[2]}-${parts[1]}`;
    }

    // For ISO strings like "2024-07-08T00:00:00.000Z", just take the date part
    return dateString.split('T')[0];
  };

  // Form setup with react-hook-form
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const form = useForm<InvoiceFormInputs>({
    resolver: zodResolver(invoiceFormSchema),
    defaultValues: {
      invoiceFor: isAgent ? 'agent' : 'client',
      currency: 'USD',
      status: 'pending',
      billDate: isAgent ? getAgentInvoiceBillDate(currentYear, currentMonth) : getClientInvoiceBillDate(currentYear, currentMonth),
      dueDate: isAgent ? getAgentInvoiceDueDate(currentYear, currentMonth) : getClientInvoiceDueDate(currentYear, currentMonth),
      servicePeriod: {
        startDate: getCurrentMonthFirstDate(),
        endDate: getCurrentMonthLastDate(),
      },
      lineItems: [],
      subtotal: 0,
      totalAmount: 0,
      paidAmount: 0,
      trialAdjustment: {
        trialDays: 0,
        adjustmentAmount: 0,
        note: '',
        applied: false,
      },
    },
  });

  const {
    register,
    control,
    handleSubmit,
    setValue,
    watch,
    getValues,
    formState: { errors },
    reset,
  } = form;

  const {
    fields: lineItemFields,
    append: appendLineItem,
    remove: removeLineItem,
  } = useFieldArray({
    control,
    name: 'lineItems',
  });

  // Watch values for calculations
  const lineItems = watch('lineItems');
  const paidAmount = watch('paidAmount');
  const trialAdjustment = watch('trialAdjustment');
  const selectedClientId = watch('clientId');
  const selectedAgentId = watch('agentId');
  const selectedCurrency = watch('currency') || 'USD';

  // Initialize form with existing invoice data
  useEffect(() => {
    if (!invoice) return;

    const formattedInvoice = {
      ...invoice,
      billDate: parseAndFormatDate(invoice.billDate),
      dueDate: parseAndFormatDate(invoice.dueDate),
      servicePeriod: {
        ...invoice.servicePeriod,
        startDate: parseAndFormatDate(invoice.servicePeriod.startDate),
        endDate: parseAndFormatDate(invoice.servicePeriod.endDate),
      },
      // Ensure required fields have default values
      currency: invoice.currency || 'USD',
      status: invoice.status || 'pending',
      subtotal: invoice.subtotal || 0,
      totalAmount: invoice.totalAmount || 0,
      paidAmount: invoice.paidAmount || 0,
      lineItems: invoice.lineItems || [],
      trialAdjustment: invoice.trialAdjustment || {
        trialDays: 0,
        adjustmentAmount: 0,
        note: '',
        applied: false,
      },
    };

    reset(formattedInvoice);
  }, [invoice, reset]);

  // Calculate totals when line items, paid amount, or trial adjustment changes
  useEffect(() => {
    const subtotal = lineItems.reduce(
      (acc, item) => acc + (item.amount || 0),
      0
    );
    const adjustmentAmount = trialAdjustment?.applied
      ? trialAdjustment.adjustmentAmount || 0
      : 0;
    const totalAmount = subtotal - adjustmentAmount;

    setValue('subtotal', subtotal, { shouldValidate: false });
    setValue('totalAmount', totalAmount, { shouldValidate: false });
  }, [
    lineItems,
    trialAdjustment?.applied,
    trialAdjustment?.adjustmentAmount,
    setValue,
  ]);

  // Separate effect to update calculations when individual line item amounts change
  useEffect(() => {
    const recalculateAmounts = () => {
      const allLineItems = getValues('lineItems');
      const subtotal = allLineItems.reduce(
        (acc, item) => acc + (item.amount || 0),
        0
      );
      const adjustmentAmount = trialAdjustment?.applied
        ? trialAdjustment.adjustmentAmount || 0
        : 0;
      const totalAmount = subtotal - adjustmentAmount;

      setValue('subtotal', subtotal, { shouldValidate: false });
      setValue('totalAmount', totalAmount, { shouldValidate: false });
    };

    // Trigger recalculation
    recalculateAmounts();
  }, [
    lineItems?.length,
    getValues,
    setValue,
    trialAdjustment?.applied,
    trialAdjustment?.adjustmentAmount,
  ]);

  // Get options for dropdowns
  const data = getBrandsForClient(brands?.brands || [], selectedClientId || '');

  // Fix client options to handle proper array access
  let clientOptions =
    clients?.clients?.map?.((c: any) => ({ value: c._id, label: c.name })) ||
    [];

  let agentOptions =
    agents?.agents?.map((a: any) => ({ value: a._id, label: a.name })) || [];

  if (!isAgent) {
    let filteredAgents = filterAgentsForBrands(
      agents?.agents || [],
      data || []
    );

    const agentOptionsWithPay = filteredAgents.map((a: IAgent) => ({
      value: a._id!,
      label: a.name!,
      pay: getAgentPay(
        agentBrands?.agentBrands || [],
        a._id!,
        'agentId',
        'fromClient'
      ),
    }));

    agentOptions = agentOptionsWithPay;
  }

  let brandOptions =
    brands?.brands?.map((b: any) => ({ value: b._id, label: b.name })) || [];

  if (isAgent) {
    const getAgentKimId = agents?.agents?.find(
      (a: any) => a._id === selectedAgentId
    )?.kimId;
    let filteredBrands = brands?.brands?.filter((b: any) =>
      b.agents?.includes(getAgentKimId)
    );

    const brandOptionsWithPay = filteredBrands?.map((b: any) => ({
      value: b._id,
      label: b.name,
      pay: getAgentPay(
        agentBrands?.agentBrands || [],
        b._id!,
        'brandId',
        'toAgent'
      ),
    }));

    brandOptions = brandOptionsWithPay || [];
  }

  // Line item handlers
  const handleLineItemChange = (index: number, field: string, value: any) => {
    const lineItemsArray = getValues('lineItems');
    const updatedItem = { ...lineItemsArray[index] };

    if (field === 'sourceReference') {
      const [subField, subValue] = value.split('.');
      updatedItem.sourceReference = {
        ...updatedItem.sourceReference,
        [subField]: subValue,
      };

      // Auto-generate name for service_hours type
      if (updatedItem.itemType === 'service_hours' && subValue) {
        if (isAgent) {
          const selectedBrand = brandOptions.find(
            (opt: any) => opt.value === subValue
          );
          if (selectedBrand) {
            updatedItem.name = `Agent services - ${selectedBrand.label}`;
            if ((selectedBrand as any).pay !== undefined) {
              updatedItem.rate = (selectedBrand as any).pay;
              const quantity = parseFloat(String(updatedItem.quantity));
              if (!isNaN(quantity)) {
                updatedItem.amount = quantity * (selectedBrand as any).pay;
              }
            }
          }
        } else {
          const selectedAgent = agentOptions.find(
            (opt: any) => opt.value === subValue
          );
          if (selectedAgent) {
            updatedItem.name = `Customer Support Hours - ${selectedAgent.label}`;
            if ((selectedAgent as any).pay !== undefined) {
              updatedItem.rate = (selectedAgent as any).pay;
              const quantity = parseFloat(String(updatedItem.quantity));
              if (!isNaN(quantity)) {
                updatedItem.amount = quantity * (selectedAgent as any).pay;
              }
            }
          }
        }
      }
    } else {
      if (field === 'rate' || field === 'quantity') {
        (updatedItem as any)[field] = value;
        const quantity = parseFloat(String(updatedItem.quantity));
        const rate = parseFloat(String(updatedItem.rate));
        updatedItem.amount =
          !isNaN(quantity) && !isNaN(rate) ? quantity * rate : 0;
      } else if (field === 'itemType') {
        (updatedItem as any)[field] = value;
        if (value === 'service_hours') {
          const agentOrBrandId = isAgent
            ? updatedItem.sourceReference?.brandId
            : updatedItem.sourceReference?.agentId;

          if (agentOrBrandId) {
            const selectedOption = isAgent
              ? brandOptions.find((opt: any) => opt.value === agentOrBrandId)
              : agentOptions.find((opt: any) => opt.value === agentOrBrandId);

            if (selectedOption) {
              updatedItem.name = isAgent
                ? `Agent services - ${selectedOption.label}`
                : `Customer Support Hours - ${selectedOption.label}`;
            }
          } else {
            updatedItem.name = isAgent ? 'Agent services' : 'Customer Support Hours';
          }
        }
      } else {
        (updatedItem as any)[field] = value;
      }
    }

    setValue(`lineItems.${index}`, updatedItem);

    // Trigger immediate recalculation
    const updatedLineItems = getValues('lineItems');
    updatedLineItems[index] = updatedItem;
    const subtotal = updatedLineItems.reduce(
      (acc, item) => acc + (item.amount || 0),
      0
    );
    const adjustmentAmount = trialAdjustment?.applied
      ? trialAdjustment.adjustmentAmount || 0
      : 0;
    const totalAmount = subtotal - adjustmentAmount;

    setValue('subtotal', subtotal, { shouldValidate: false });
    setValue('totalAmount', totalAmount, { shouldValidate: false });
  };

  const addNewLineItem = () => {
    appendLineItem({
      itemType: 'adjustment',
      name: '',
      quantity: 0,
      rate: 0,
      unit: 'period',
      amount: 0,
    });
  };

  // Create separate payloads for create and update
  const createPayload = (formData: InvoiceFormInputs): CreateInvoicePayload => {
    const toISO = (date: string) => {
      if (!date) return '';
      if (date.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const utcDate = new Date(date);
        return utcDate.toISOString();
      }
      const parsed = parseDate(date);
      return isNaN(parsed.getTime()) ? '' : parsed.toISOString();
    };

    const payload: CreateInvoicePayload = {
      invoiceFor: formData.invoiceFor,
      currency: formData.currency,
      billDate: isEditing ? toISO(formData.billDate) : getCurrentDateTime(),
      dueDate: toISO(formData.dueDate),
      servicePeriod: {
        startDate: toISO(formData.servicePeriod.startDate),
        endDate: toISO(formData.servicePeriod.endDate),
      },
      status: formData.status,
      paidAmount: formData.paidAmount,
      totalAmount: formData.totalAmount,
      subtotal: formData.subtotal,
      lineItems: formData.lineItems.map(({ sourceReference, ...rest }) => {
        let cleanedSourceReference: any = undefined;

        if (isAgent && sourceReference?.brandId) {
          cleanedSourceReference = { brandId: sourceReference.brandId };
        } else if (!isAgent && sourceReference?.agentId) {
          cleanedSourceReference = { agentId: sourceReference.agentId };
        }

        return {
          ...rest,
          ...(cleanedSourceReference && {
            sourceReference: cleanedSourceReference,
          }),
        };
      }),
    };

    // Add agent or client ID based on invoice type
    if (isAgent && formData.agentId) {
      payload.agentId = formData.agentId;
    } else if (!isAgent && formData.clientId) {
      payload.clientId = formData.clientId;
    }

    // Add trial adjustment if applied
    if (formData.trialAdjustment?.applied) {
      payload.trialAdjustment = formData.trialAdjustment;
    }

    // Add billTo for client invoices
    if (!isAgent && formData.billTo) {
      payload.billTo = formData.billTo;
    }

    return payload;
  };

  const updatePayload = (formData: InvoiceFormInputs): any => {
    const toISO = (date: string) => {
      if (!date) return '';
      if (date.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const utcDate = new Date(date);
        return utcDate.toISOString();
      }
      const parsed = parseDate(date);
      return isNaN(parsed.getTime()) ? '' : parsed.toISOString();
    };

    // For updates, preserve the original billDate timestamp if only the date part matches
    const preserveBillDateTimestamp = (
      formDateString: string,
      originalDateString?: string
    ) => {
      if (!originalDateString) return toISO(formDateString);

      const formDateOnly = formDateString.split('T')[0];
      const originalDateOnly = originalDateString.split('T')[0];

      // If the date part matches the original, preserve the original timestamp
      if (formDateOnly === originalDateOnly) {
        return originalDateString;
      }

      // If date was actually changed, use the form date but preserve time from original
      const originalTime = originalDateString.split('T')[1] || '00:00:00.000Z';
      return `${formDateOnly}T${originalTime}`;
    };

    const payload: CreateInvoicePayload = {
      invoiceFor: formData.invoiceFor,
      currency: formData.currency,
      billDate: preserveBillDateTimestamp(formData.billDate, invoice?.billDate),
      dueDate: toISO(formData.dueDate),
      servicePeriod: {
        startDate: toISO(formData.servicePeriod.startDate),
        endDate: toISO(formData.servicePeriod.endDate),
      },
      status: formData.status,
      paidAmount: formData.paidAmount,
      totalAmount: formData.totalAmount,
      subtotal: formData.subtotal,
      lineItems: formData.lineItems.map(({ sourceReference, ...rest }) => {
        let cleanedSourceReference: any = undefined;

        if (isAgent && sourceReference?.brandId) {
          cleanedSourceReference = { brandId: sourceReference.brandId };
        } else if (!isAgent && sourceReference?.agentId) {
          cleanedSourceReference = { agentId: sourceReference.agentId };
        }

        return {
          ...rest,
          ...(cleanedSourceReference && {
            sourceReference: cleanedSourceReference,
          }),
        };
      }),
    };

    // Add agent or client ID based on invoice type
    if (isAgent && formData.agentId) {
      payload.agentId = formData.agentId;
    } else if (!isAgent && formData.clientId) {
      payload.clientId = formData.clientId;
    }

    // Add trial adjustment if applied
    if (formData.trialAdjustment?.applied) {
      payload.trialAdjustment = formData.trialAdjustment;
    }

    return payload;
  };

  const onSubmit = (data: InvoiceFormInputs) => {
    if (isEditing) {
      const payload = updatePayload(data);
      update(payload);
    } else {
      const payload = createPayload(data);
      create(payload);
    }
  };

  const handlePdfPreview = () => {
    window.open(
      `${backendUrl}/invoice/pdf/preview/${invoice?.invoiceFor}/${invoice?.id}`,
      '_blank'
    );
  };

  const currentSubtotal = watch('subtotal') || 0;
  const currentTotalAmount = watch('totalAmount') || 0;
  const currentPaidAmount = watch('paidAmount') || 0;
  const balanceAmount = currentTotalAmount - currentPaidAmount;

  return (
    <div className="max-w-6xl mx-auto">
      <FormProvider {...form}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="bg-white dark:bg-gray-900 shadow-sm border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
        >
          {/* Main Content */}
          <div className="px-6 py-4 space-y-4 text-sm">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {isEditing && (
                <InputComponent
                  label="Invoice Number"
                  disabled
                  value={invoice?.invoiceNumber || ''}
                  onChange={() => {}}
                  className="bg-[#F9F9F9]"
                />
              )}

              <div>
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                  Select {isAgent ? 'Agent' : 'Client'} *
                </label>
                <Controller
                  name={isAgent ? 'agentId' : 'clientId'}
                  control={control}
                  rules={{
                    required: `${isAgent ? 'Agent' : 'Client'} is required`,
                  }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      value={
                        isAgent
                          ? agentOptions.find(
                              (opt: any) => opt.value === field.value
                            ) || null
                          : clientOptions.find(
                              (opt: any) => opt.value === field.value
                            ) || null
                      }
                      onChange={(option: any) =>
                        field.onChange(option?.value || '')
                      }
                      options={isAgent ? agentOptions : clientOptions}
                      placeholder={`Select ${isAgent ? 'agent' : 'client'}`}
                      className="react-select-container"
                      classNamePrefix="react-select"
                      styles={{
                        control: (base) => ({
                          ...base,
                          minHeight: '38px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          '&:hover': { borderColor: '#9ca3af' },
                        }),
                        menu: (base) => ({ ...base, zIndex: 50 }),
                      }}
                    />
                  )}
                />
                {errors.agentId && (
                  <span className="text-red-500 text-xs">
                    {errors.agentId.message}
                  </span>
                )}
                {errors.clientId && (
                  <span className="text-red-500 text-xs">
                    {errors.clientId.message}
                  </span>
                )}
              </div>

              <div>
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                  Currency *
                </label>
                <Controller
                  name="currency"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      value={
                        currencyOptions.find(
                          (opt) => opt.value === field.value
                        ) || null
                      }
                      onChange={(option: any) =>
                        field.onChange(option?.value || 'USD')
                      }
                      options={currencyOptions}
                      className="react-select-container"
                      classNamePrefix="react-select"
                      styles={{
                        control: (base) => ({
                          ...base,
                          minHeight: '38px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          '&:hover': { borderColor: '#9ca3af' },
                        }),
                      }}
                    />
                  )}
                />
                {errors.currency && (
                  <span className="text-red-500 text-xs">
                    {errors.currency.message}
                  </span>
                )}
              </div>
            </div>

            {/* Service Period & Dates */}
            <div className="bg-greyTen border border-greyThirty dark:bg-gray-800 rounded-lg p-3">
              <h3 className="text-sm font-medium text-blackFourty dark:text-white mb-3">
                Service Period & Billing Dates
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                    Service Start Date *
                  </label>
                  <input
                    type="date"
                    {...register('servicePeriod.startDate')}
                    className="w-full rounded-md px-3 py-2 shadow-sm focus:outline-none border border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
                  />
                  {errors.servicePeriod?.startDate && (
                    <span className="text-red-500 text-xs">
                      {errors.servicePeriod.startDate.message}
                    </span>
                  )}
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                    Service End Date *
                  </label>
                  <input
                    type="date"
                    {...register('servicePeriod.endDate')}
                    className="w-full rounded-md px-3 py-2 shadow-sm focus:outline-none border border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
                  />
                  {errors.servicePeriod?.endDate && (
                    <span className="text-red-500 text-xs">
                      {errors.servicePeriod.endDate.message}
                    </span>
                  )}
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                    Bill Date *
                  </label>
                  <input
                    type="date"
                    {...register('billDate')}
                    className="w-full rounded-md px-3 py-2 shadow-sm focus:outline-none border border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
                  />
                  {errors.billDate && (
                    <span className="text-red-500 text-xs">
                      {errors.billDate.message}
                    </span>
                  )}
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                    Due Date *
                  </label>
                  <input
                    type="date"
                    {...register('dueDate')}
                    className="w-full rounded-md px-3 py-2 shadow-sm focus:outline-none border border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
                  />
                  {errors.dueDate && (
                    <span className="text-red-500 text-xs">
                      {errors.dueDate.message}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Line Items */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-blackFourty dark:text-white">
                  Line Items
                </h3>
                <button
                  type="button"
                  onClick={addNewLineItem}
                  className="text-indigo-600 mr-2"
                >
                  <CirclePlus size={16} />
                </button>
              </div>

              {/* Line Items Table */}
              <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                {/* Table Header */}
                <div className="bg-gray-50 dark:bg-gray-800 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                  <div className="grid grid-cols-12 gap-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    <div className="col-span-4">
                      Item Name / {isAgent ? 'Brand' : 'Agent'}
                    </div>
                    <div className="col-span-2">Type</div>
                    <div className="col-span-2"></div>
                    <div className="col-span-1 text-center">Qty</div>
                    <div className="col-span-1 text-center">Rate</div>
                    <div className="col-span-1 text-right">Amount</div>
                    <div className="col-span-1 text-center">Action</div>
                  </div>
                </div>

                {/* Table Body */}
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {lineItemFields.map((item, index) => (
                    <div key={item.id} className="px-4 py-3">
                      <div className="grid grid-cols-12 gap-3 items-center">
                        {/* Item name OR Agent/Brand selector */}
                        <div className="col-span-4">
                          {watch(`lineItems.${index}.itemType`) ===
                          'service_hours' ? (
                            <Controller
                              name={`lineItems.${index}.sourceReference.${isAgent ? 'brandId' : 'agentId'}`}
                              control={control}
                              render={({ field }) => (
                                <Select
                                  {...field}
                                  value={
                                    isAgent
                                      ? brandOptions.find(
                                          (opt: any) =>
                                            opt.value === field.value
                                        ) || null
                                      : agentOptions.find(
                                          (opt: any) =>
                                            opt.value === field.value
                                        ) || null
                                  }
                                  onChange={(option: any) => {
                                    field.onChange(option?.value || '');
                                    handleLineItemChange(
                                      index,
                                      'sourceReference',
                                      `${isAgent ? 'brandId' : 'agentId'}.${option?.value || ''}`
                                    );
                                  }}
                                  options={
                                    isAgent ? brandOptions : agentOptions
                                  }
                                  placeholder={`Select ${isAgent ? 'brand' : 'agent'}`}
                                  className="react-select-container"
                                  classNamePrefix="react-select"
                                  menuPortalTarget={document.body}
                                  styles={{
                                    control: (base) => ({
                                      ...base,
                                      minHeight: '38px',
                                      border: '1px solid #d1d5db',
                                      borderRadius: '6px',
                                    }),
                                    menu: (base) => ({
                                      ...base,
                                      zIndex: 9999,
                                    }),
                                    menuPortal: (base) => ({
                                      ...base,
                                      zIndex: 9999,
                                    }),
                                  }}
                                />
                              )}
                            />
                          ) : (
                            <input
                              {...register(`lineItems.${index}.name`)}
                              placeholder="Item name"
                              className="w-full rounded-md px-3 py-2 shadow-sm focus:outline-none border border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
                            />
                          )}
                        </div>

                        {/* Item type */}
                        <div className="col-span-2">
                          <Controller
                            name={`lineItems.${index}.itemType`}
                            control={control}
                            render={({ field }) => (
                              <Select
                                {...field}
                                value={
                                  lineItemTypeOptions.find(
                                    (opt) => opt.value === field.value
                                  ) || null
                                }
                                onChange={(option: any) => {
                                  field.onChange(option?.value || '');
                                  handleLineItemChange(
                                    index,
                                    'itemType',
                                    option?.value || ''
                                  );
                                }}
                                options={lineItemTypeOptions}
                                className="react-select-container"
                                classNamePrefix="react-select"
                                menuPortalTarget={document.body}
                                styles={{
                                  control: (base) => ({
                                    ...base,
                                    minHeight: '38px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '6px',
                                  }),
                                  menu: (base) => ({
                                    ...base,
                                    zIndex: 9999,
                                  }),
                                  menuPortal: (base) => ({
                                    ...base,
                                    zIndex: 9999,
                                  }),
                                }}
                              />
                            )}
                          />
                        </div>

                        <div className="col-span-2"></div>

                        {/* Quantity */}
                        <div className="col-span-1">
                          <input
                            type="number"
                            step="any"
                            {...register(`lineItems.${index}.quantity`, {
                              valueAsNumber: true,
                              onChange: (e) =>
                                handleLineItemChange(
                                  index,
                                  'quantity',
                                  parseFloat(e.target.value) || 0
                                ),
                            })}
                            placeholder="0"
                            className="w-full rounded-md px-3 py-2 shadow-sm focus:outline-none border border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 text-center"
                          />
                        </div>

                        {/* Rate */}
                        <div className="col-span-1">
                          <input
                            type="number"
                            step="any"
                            {...register(`lineItems.${index}.rate`, {
                              valueAsNumber: true,
                              onChange: (e) =>
                                handleLineItemChange(
                                  index,
                                  'rate',
                                  parseFloat(e.target.value) || 0
                                ),
                            })}
                            placeholder="0"
                            className="w-full rounded-md px-3 py-2 shadow-sm focus:outline-none border border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 text-right"
                          />
                        </div>

                        {/* Amount */}
                        <div className="col-span-1 text-right">
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {CURRENCY_SYMBOLS[selectedCurrency] || selectedCurrency} {watch(`lineItems.${index}.amount`)?.toFixed(1) || '0.0'}
                          </span>
                        </div>

                        {/* Remove button */}
                        <div className="col-span-1 text-center">
                          <button
                            type="button"
                            onClick={() => removeLineItem(index)}
                            className="text-red-500 hover:text-red-700 p-2 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}

                  {lineItemFields.length === 0 && (
                    <div className="px-4 py-8 text-center">
                      <p className="text-gray-500 dark:text-gray-400">
                        No line items added yet.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Status and Totals Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                {/* Status Section */}
                <div className="mb-4">
                  <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                    Invoice Status
                  </label>
                  <Controller
                    name="status"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        value={
                          invoiceStatusOptions.find(
                            (opt) => opt.value === field.value
                          ) || null
                        }
                        onChange={(option: any) =>
                          field.onChange(option?.value || 'pending')
                        }
                        options={invoiceStatusOptions}
                        className="react-select-container"
                        classNamePrefix="react-select"
                        styles={{
                          control: (base) => ({
                            ...base,
                            minHeight: '38px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                          }),
                        }}
                      />
                    )}
                  />
                </div>

                {/* Trial Adjustment Toggle */}
                <div className="flex items-center space-x-3 mb-5">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Apply Trial Adjustment
                  </label>
                  <input
                    type="checkbox"
                    {...register('trialAdjustment.applied')}
                    className="toggle-checkbox size-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500"
                  />
                </div>

                {/* Trial Adjustment Details */}
                {watch('trialAdjustment.applied') && (
                  <div className="bg-greyTen border border-greyThirty dark:bg-gray-800 rounded-lg p-3">
                    <h3 className="text-sm font-medium text-blackFourty dark:text-white mb-3">
                      Trial Adjustment
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-6 gap-4 text-xs">
                      <div className="md:col-span-1">
                        <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                          Trial Days
                        </label>
                        <input
                          type="number"
                          {...register('trialAdjustment.trialDays', {
                            valueAsNumber: true,
                          })}
                          min={0}
                          className="w-full rounded-md px-3 py-2 shadow-sm focus:outline-none border border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
                        />
                      </div>
                      <div className="md:col-span-1">
                        <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                          Amount
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          {...register('trialAdjustment.adjustmentAmount', {
                            valueAsNumber: true,
                          })}
                          min={0}
                          className="w-full rounded-md px-3 py-2 shadow-sm focus:outline-none border border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
                        />
                      </div>
                      <div className="md:col-span-4">
                        <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                          Note
                        </label>
                        <input
                          {...register('trialAdjustment.note')}
                          placeholder="Optional note"
                          className="w-full rounded-md px-3 py-2 shadow-sm focus:outline-none border border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Totals Section */}
              <div className="bg-greyTen border border-greyThirty dark:bg-gray-800 rounded-lg p-3">
                <h3 className="text-base font-semibold text-blackFourty dark:text-white mb-4">
                  Summary
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">
                      Subtotal:
                    </span>
                    <span className="font-medium text-blackFourty dark:text-white">
                      {CURRENCY_SYMBOLS[selectedCurrency] || selectedCurrency} {currentSubtotal.toFixed(1)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">
                      Trial Adjustment:
                    </span>
                    <span className="font-medium text-blackFourty dark:text-white">
                      -{CURRENCY_SYMBOLS[selectedCurrency] || selectedCurrency} {(trialAdjustment?.applied ? trialAdjustment.adjustmentAmount || 0 : 0).toFixed(1)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm font-semibold">
                    <span className="text-blackFourty dark:text-white">
                      Total:
                    </span>
                    <span className="text-blackFourty dark:text-white">
                      {CURRENCY_SYMBOLS[selectedCurrency] || selectedCurrency} {currentTotalAmount.toFixed(1)}
                    </span>
                  </div>
                  {!isAgent && (
                    <>
                      <div className="flex justify-between items-center">
                        <label className="text-sm text-gray-500 dark:text-gray-400">
                          Paid Amount:
                        </label>
                        <div className="w-16">
                          <input
                            type="number"
                            step="0.01"
                            min={0}
                            {...register('paidAmount', { valueAsNumber: true })}
                            className="w-full rounded-md px-3 py-2 shadow-sm focus:outline-none border border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 text-right"
                          />
                        </div>
                      </div>
                      <div className="border-t border-gray-200 dark:border-gray-600 pt-3">
                        <div className="flex justify-between text-lg font-medium">
                          <span className="text-gray-900 dark:text-white text-base">
                            Balance Due:
                          </span>
                          <span className="text-blackFourty dark:text-white text-base">
                            {CURRENCY_SYMBOLS[selectedCurrency] || selectedCurrency} {balanceAmount.toFixed(1)}
                          </span>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
            {isEditing && (
              <BtnPrimary type="button" onClick={handlePdfPreview}>
                Invoice PDF
              </BtnPrimary>
            )}
            <BtnPrimary
              type="submit"
              disabled={isCreating || isUpdating}
              className="flex items-center gap-2 px-6 py-2"
            >
              {isCreating || isUpdating ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {isEditing ? 'Update Invoice' : 'Create Invoice'}
            </BtnPrimary>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default InvoiceForm;
